import { _DEBUG } from './constants';
import { fetchProductDetail } from './fetchProductDetail';
import { debounce, insertDotLoaderElement, parsePrice } from './utils';

/**
 * Find the amount input element on the page
 *
 * @returns {HTMLInputElement|null} The amount input element or null if not found
 */
function findAmountInput() {
  // 1. First try form-based (aponeo_de style)
  let amountInput = document.forms['form_warenkorb']?.elements['amnt'];

  // 2. If not found, search by class
  if (!amountInput) {
    amountInput = document.querySelector('.js-amount__input');
  }

  // 3. Fallback: search by name
  if (!amountInput) {
    amountInput = document.querySelector('input[name="amnt"]');
  }

  return amountInput || null;
}

/**
 * Find the current tier based on quantity
 * @param {number} quantity - The selected quantity
 * @returns {object|null} The matching tier or null if none found
 */
function findCurrentTier(quantity) {
  let currentTier = productPriceTiers[0];
  let highestMatchingAmount = -1;

  for (const tier of productPriceTiers) {
    if (quantity >= tier.amount && tier.amount > highestMatchingAmount) {
      currentTier = tier;
      highestMatchingAmount = tier.amount;
    }
  }

  if (!currentTier) {
    if (_DEBUG) console.log('no tier found for amount:', quantity);
    return null;
  }

  return currentTier;
}

/**
 * Update the DOM with tier information
 * @param {object} tier - The tier object with price and savings data
 * @param {number} amount - The selected quantity
 */
function updateTierDisplay(tier, amount) {
  const pdpProductSaleprice = tier.saleprice.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // Update all price elements
  const priceElements = document.querySelectorAll('.js-tier-price-display');
  if (priceElements.length > 0) {
    for (const element of priceElements) {
      element.textContent = `${pdpProductSaleprice} €`;
    }

    if (_DEBUG) console.log('Updated price elements:', priceElements);
  } else if (_DEBUG) {
    if (_DEBUG) console.warn('Could not find price elements with class .js-tier-price-display');
  }

  // Update all baseprice elements
  const basepriceElements = document.querySelectorAll('.js-baseprice-info');
  if (basepriceElements.length > 0) {
    if (tier.baseprice !== undefined) {
      for (const element of basepriceElements) {
        element.textContent = tier.baseprice;
      }
    }

    if (_DEBUG) console.log('Updated baseprice elements:', basepriceElements);
  } else {
    if (_DEBUG) console.warn('Could not find baseprice elements with class .js-baseprice-info');
  }

  // Update tier saving percent spans
  const savingPercentElements = document.querySelectorAll('.js-tier-saving-percent');
  if (savingPercentElements.length > 0) {
    if (tier.saving_additional_percent !== undefined) {
      const value = parseFloat(tier.saving_additional_percent);
      const precision = value < 0.5 ? 1 : 0;

      const formattedSavingPercent = value.toLocaleString('de-DE', {
        minimumFractionDigits: precision,
        maximumFractionDigits: precision,
      });

      for (const element of savingPercentElements) {
        element.textContent = `-${formattedSavingPercent}%`;
      }
    } else {
      for (const element of savingPercentElements) {
        element.textContent = '';
      }
    }

    if (_DEBUG) console.log('Updated saving percent elements:', savingPercentElements);
  } else if (_DEBUG) {
    if (_DEBUG) console.warn('Could not find saving percent elements with class .js-tier-saving-percent');
  }

  // Update tier saving value spans
  const savingValueElements = document.querySelectorAll('.js-tier-saving-value-display');
  if (savingValueElements.length > 0) {
    for (const element of savingValueElements) {
      if (typeof tier.saving_additional_euro === 'undefined' || tier.saving_additional_euro === 0) {
        element.textContent = '';
        continue;
      }

      const totalSaving = tier.saving_additional_euro * amount;

      const formattedSavingValue = totalSaving.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      element.textContent = `${formattedSavingValue} €`;
    }

    if (_DEBUG) console.log('Updated saving value elements:', savingValueElements);
  } else {
    if (_DEBUG) console.warn('Could not find saving value elements with class .js-tier-saving-value-display');
  }
}

/**
 * Update the price display when tier is selected based on quantity
 *
 * @param {number} amount - The selected quantity
 */
function updateTierDetails(amount) {
  if (_DEBUG) console.log('--->showTierInformation for amount:', amount);

  if (typeof productPriceTiers === 'undefined') {
    if (_DEBUG) console.log('no tiers found');
    return;
  }

  const currentTier = findCurrentTier(amount);

  if (!currentTier) {
    return;
  }

  if (_DEBUG) console.log('current tier:', currentTier);
  if (_DEBUG) console.log('saleprice:', currentTier.saleprice);

  updateTierDisplay(currentTier, amount);

  if (_DEBUG) console.log('tier starts:', currentTier.amount);
  if (_DEBUG) console.log('saving_percent against UVP/ARP:', currentTier.saving_percent);
  if (_DEBUG) console.log('saving_percent extra:', currentTier.saving_additional_percent);
  if (_DEBUG) console.log('saving_euro extra:', currentTier.saving_additional_euro);
  if (_DEBUG) console.log('--->Eof showTierInformation for amount:', amount);
}

/**
 * Update the active tier button based on the current amount
 * Marks the highest tier button whose data-amount is <= current amount as active
 *
 * @param {number} amount - The current quantity
 */
function updateActiveTierButton(amount) {
  const tierButtons = document.querySelectorAll('.js-tier-btn');

  if (tierButtons.length === 0) {
    if (_DEBUG) console.log('updateActiveTierButton: no tier buttons found');
    return;
  }

  let activeTierButton = null;
  let highestMatchingAmount = -1;

  for (const button of tierButtons) {
    const tierAmount = parseInt(button.dataset.amount, 10);
    if (!isNaN(tierAmount) && amount >= tierAmount && tierAmount > highestMatchingAmount) {
      activeTierButton = button;
      highestMatchingAmount = tierAmount;
    }
  }

  if (_DEBUG) console.log('updateActiveTierButton: highest matching amount:', highestMatchingAmount);

  for (const button of tierButtons) {
    button.classList.remove('active');
  }

  if (activeTierButton) {
    activeTierButton.classList.add('active');
    if (_DEBUG) console.log('updateActiveTierButton: activated button for tier:', highestMatchingAmount);
  }
}

/**
 * Fetch product detail and dispatch custom event
 * @param {string|number} productId - The product ID
 * @param {number} amount - The requested quantity
 * @param {AbortSignal} signal - AbortSignal to cancel the request
 */
async function fetchAndDispatchProductDetail(productId, amount, signal) {
  if (!productId) {
    return;
  }

  try {
    document.dispatchEvent(
      new CustomEvent('product-detail-update-start', {
        detail: { productId, amount },
      }),
    );

    if (_DEBUG) console.log(`tierPriceUpdater: fetching product detail (${productId}) for amount: ${amount}`);

    const result = await fetchProductDetail(productId, amount, signal);

    document.dispatchEvent(
      new CustomEvent('product-detail-update', {
        detail: { productId, amount, result },
      }),
    );

    if (_DEBUG) console.log('tierPriceUpdater: product detail event dispatched');
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('tierPriceUpdater: error fetching product detail:', error);

      document.dispatchEvent(
        new CustomEvent('product-detail-update-error', {
          detail: { error },
        }),
      );
    } else if (_DEBUG) {
      console.log('tierPriceUpdater: product detail fetch aborted');
    }
  }
}

/**
 * Update the shipping cost text when product detail updates occur
 */
function setupShippingCostTextListener() {
  const shippingCostElement = document.querySelector('.js-shipping-cost-text');

  if (!shippingCostElement) {
    if (_DEBUG) console.log('setupShippingCostTextListener: shipping cost element not found');
    return;
  }

  const freeShippingThreshold = parseFloat(shippingCostElement.dataset.freeShippingThreshold) + 0.01;
  const templateText = shippingCostElement.dataset.shippingCostTextTemplate; // "Versandkostenfrei ab %1s"
  const freeText = shippingCostElement.dataset.shippingCostTextFree; // "Versandkostenfrei"

  const isFreeshippingUnlocked = shippingCostElement.dataset.isFreeShipping === 'true';
  const cartSubtotal = parseFloat(shippingCostElement.dataset.cartSubtotal) || 0;

  const applyDefaultState = () => {
    if (!isNaN(freeShippingThreshold) && templateText) {
      const formattedThreshold = freeShippingThreshold.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

      shippingCostElement.innerHTML = `${templateText.replace('%1s', formattedThreshold)} €`;
      shippingCostElement.dataset.isFreeShipping = 'false';
    } else {
      shippingCostElement.innerHTML = 'Fehler beim Berechnen der Versandkosten';
      shippingCostElement.dataset.isFreeShipping = 'false';
    }
  };

  const hasTiers = typeof productPriceTiers !== 'undefined' && productPriceTiers.length > 1;

  if (hasTiers) {
    // Handle tiers case: listen to product detail update event

    document.addEventListener('product-detail-update-start', () => {
      insertDotLoaderElement(shippingCostElement);
    });

    document.addEventListener('product-detail-update', (event) => {
      const { result, amount } = event.detail;

      if (result?.ProdDetailVersandkostenfreiText && result?.ProdDetailVersandkostenfrei !== undefined) {
        shippingCostElement.innerHTML = result.ProdDetailVersandkostenfreiText;
        shippingCostElement.dataset.isFreeShipping = result.ProdDetailVersandkostenfrei ? 'true' : 'false';
      }
    });

    document.addEventListener('product-detail-update-error', () => {
      applyDefaultState();
    });
  } else {
    // Handle no-tiers case: simple shipping cost calculation based on total price
    const amountInput = findAmountInput();

    if (!amountInput) {
      return;
    }

    if (isNaN(freeShippingThreshold) || !templateText || !freeText) {
      if (_DEBUG) {
        console.warn('setupShippingCostTextListener: missing data attributes', {
          threshold: freeShippingThreshold,
          templateText,
          freeText,
        });
      }

      return;
    }

    const updateShippingText = () => {
      const amount = parseInt(amountInput?.value, 10) || 1;
      const priceElement = document.querySelector('.js-tier-price-display');

      if (!priceElement) {
        return;
      }

      const price = parsePrice(priceElement.textContent);

      if (isNaN(price)) {
        return;
      }

      const productTotal = price * amount;
      const totalCartValue = cartSubtotal + productTotal;

      if (totalCartValue >= freeShippingThreshold || isFreeshippingUnlocked) {
        shippingCostElement.innerHTML = freeText;
        shippingCostElement.dataset.isFreeShipping = 'true';
      } else {
        applyDefaultState();
      }
    };

    amountInput.addEventListener('change', updateShippingText);
  }

  const renderDefaultText = (el, template, threshold) => {
    const formattedThreshold = threshold.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    el.innerHTML = template.replace('%1s', formattedThreshold) + ' €';
  };
}

/**
 * Initialize tier price updater
 * Listens for amount input changes and updates the price display
 */
export default function initTierPriceUpdater() {
  const amountInput = findAmountInput();

  if (!amountInput) {
    if (_DEBUG) console.log('tierPriceUpdater: amount-input not found');
    return;
  }

  if (_DEBUG) console.log('tierPriceUpdater: amount-input found');

  const productId = document.getElementById('abskt')?.dataset.internalId;
  const abortControllerRef = { current: null };

  const debouncedFetchProductDetail = debounce((amount) => {
    if (!productId) {
      return;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    fetchAndDispatchProductDetail(productId, amount, abortControllerRef.current.signal);
  }, 300);

  const onAmountValueChanged = () => {
    const amount = parseInt(amountInput.value, 10) || 1;
    updateTierDetails(amount);
    updateActiveTierButton(amount);
    debouncedFetchProductDetail(amount);
  };

  amountInput.addEventListener('change', onAmountValueChanged);

  setupShippingCostTextListener();
}
